// Story 1.38 (Twitch) + Story 7.20 (YouTube) — the caller's active
// channel-ownership claim for one provider
// (GET /api/v1/creators/me/identity/<provider>).
//
// Drives the "Verify a channel (optional)" affordance on the application
// form and the verified badge on the pending card. Advisory: proving
// ownership is never required to apply — it speeds up review.
//
// ONE HOOK, PARAMETERIZED. The v1 version was `useTwitchIdentityClaim`
// with a Twitch-shaped response type (`twitch_user_id`,
// `twitch_link_enabled`). Adding YouTube that way would have meant a
// second hook, a second response type and a per-provider adapter in the
// component — and a third of each for Kick. The backend now serves one
// wire shape for every provider, so this is one hook.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type IdentityProvider = "twitch" | "youtube" | "kick";

export type IdentityClaim = {
  provider: string;
  /** The CURRENT handle — Twitch login, YouTube @handle without the @.
   *  Display only: handles get renamed and change hands. */
  login: string;
  /** The provider's IMMUTABLE id (Twitch numeric id, YouTube `UC…`
   *  channel id). This is what the proof is actually keyed on. */
  provider_account_id: string;
  display_name: string | null;
  method: string;
  verified_at: string;
};

export type IdentityClaimEnvelope = {
  data: IdentityClaim | null;
  /** Whether THIS caller may run this provider's verification flow.
   *  Per-deployment for Twitch (credentials present); per-user for
   *  YouTube, which is additionally beta-allowlisted while Google's app
   *  verification is pending. */
  link_enabled: boolean;
};

export function identityClaimQueryKey(provider: IdentityProvider) {
  return ["identity-claim", provider] as const;
}

async function fetchIdentityClaim(
  provider: IdentityProvider,
): Promise<IdentityClaimEnvelope> {
  const response = await fetch(`/api/v1/creators/me/identity/${provider}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`identity/${provider} fetch failed: ${response.status}`);
  }
  return (await response.json()) as IdentityClaimEnvelope;
}

export function useIdentityClaim(provider: IdentityProvider) {
  return useQuery({
    queryKey: identityClaimQueryKey(provider),
    queryFn: () => fetchIdentityClaim(provider),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
